import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog, Loading } from '@/kit';
import {
    useApplicationPermissions,
    useCreateApplicationPermission,
    useUpdateApplicationPermission,
    useDeleteApplicationPermission,
} from '../../../hooks/useApplicationPermissions';
import { handleApiError } from '../../../api/errorMapping';
import { Banner } from '../../../components/ui/Banner';
import { PermissionCatalogStep } from './steps/PermissionCatalogStep';
import type {
    ApplicationPermissionDraft,
    LevelDefLocal,
    UpsertApplicationPermissionRequestLocal,
} from '../../../api/types/ApplicationLocal';

interface Props {
    applicationId: string;
    levels: LevelDefLocal[];
}

function toUpsertBody(perm: ApplicationPermissionDraft): UpsertApplicationPermissionRequestLocal {
    return {
        permissionKey: perm.permissionKey,
        displayName: perm.displayName,
        description: perm.description,
        scope: perm.scope,
        ...(perm.category ? { category: perm.category } : {}),
    };
}

/**
 * Pestaña "Catálogo" del detalle de una aplicación (#3-a, Task E). Reusa el `PermissionCatalogStep` (el
 * MISMO componente del wizard de crear) pero en modo EDITAR LIVE: opera directo contra los endpoints
 * `/applications/{id}/permissions` (create/update/delete) en vez de un buffer en memoria.
 *
 * El `PermissionCatalogStep` es controlado y emite en `onChange` la lista COMPLETA resultante; acá la
 * diffeamos contra el catálogo del backend para deducir la operación atómica:
 *  - permiso nuevo (key inexistente) → `POST /permissions`.
 *  - permiso editado (misma key, distinto contenido) → `PUT /permissions/{key}`.
 *  - permiso quitado (key ausente) → NO se borra directo: se intercepta y se pide CONFIRMACIÓN explícita,
 *    porque borrar un permiso CUSTOM corta el acceso EN VIVO a TODOS los tenants onboardeados de esta app
 *    (el techo de cada usuario = rol ∩ catálogo, fresco por request; sin aviso ni rollback en el backend).
 *
 * Los permisos SYSTEM son read-only en el step (no se editan ni borran), así que el diff solo ve CUSTOM.
 */
export function ApplicationCatalogTab({ applicationId, levels }: Props) {
    const { data: permissions, isLoading, isError } = useApplicationPermissions(applicationId);
    const createMut = useCreateApplicationPermission(applicationId);
    const updateMut = useUpdateApplicationPermission(applicationId);
    const deleteMut = useDeleteApplicationPermission(applicationId);

    // Permiso CUSTOM pendiente de confirmación de borrado (null = sin diálogo abierto).
    const [pendingDelete, setPendingDelete] = useState<ApplicationPermissionDraft | null>(null);

    // El catálogo del backend, proyectado al draft que consume el step (sin applicationId/timestamps).
    const value: ApplicationPermissionDraft[] = useMemo(
        () =>
            (permissions ?? []).map((p) => ({
                permissionKey: p.permissionKey,
                displayName: p.displayName,
                description: p.description,
                scope: p.scope,
                kind: p.kind,
                ...(p.category ? { category: p.category } : {}),
            })),
        [permissions],
    );

    if (isLoading) {
        return (
            <div className="flex justify-center py-10">
                <Loading />
            </div>
        );
    }

    if (isError) {
        return (
            <Banner variant="error" title="No se pudo cargar el catálogo">
                Intenta de nuevo.
            </Banner>
        );
    }

    // Diff: deduce la operación atómica entre el catálogo actual (`value`) y el que emite el step (`next`).
    const handleChange = (next: ApplicationPermissionDraft[]): void => {
        const byKey = new Map(value.map((p) => [p.permissionKey, p]));
        const nextByKey = new Map(next.map((p) => [p.permissionKey, p]));

        // DEC-018: borrado de CUSTOM corta acceso EN VIVO → interceptar y pedir confirmación (no aplicar aún).
        for (const prev of value) {
            if (!nextByKey.has(prev.permissionKey)) {
                setPendingDelete(prev);
                return;
            }
        }

        // Alta o edición: una key nueva, o una existente con contenido distinto.
        for (const cand of next) {
            const prev = byKey.get(cand.permissionKey);
            if (!prev) {
                createMut.mutate(toUpsertBody(cand), {
                    onSuccess: () => {
                        toast.success('Permiso agregado');
                    },
                    onError: handleApiError,
                });
                return;
            }
            if (
                prev.displayName !== cand.displayName ||
                prev.description !== cand.description ||
                prev.scope !== cand.scope
            ) {
                updateMut.mutate(
                    { permissionKey: cand.permissionKey, body: toUpsertBody(cand) },
                    {
                        onSuccess: (result) => {
                            // DEC-RBAC-092: si cambió el scope, el backend recalcula en cascada los roles
                            // que usan el permiso en todos los tenants de la app y reporta las asignaciones
                            // que quedaron inconsistentes (que el admin debe reasignar).
                            const cascade = result.cascade;
                            const recomputed = cascade?.rolesRecomputed.length ?? 0;
                            const inconsistent = cascade?.inconsistentAssignments.length ?? 0;
                            toast.success(
                                recomputed > 0
                                    ? `Permiso actualizado · ${String(recomputed)} rol(es) recalculado(s)`
                                    : 'Permiso actualizado',
                            );
                            if (inconsistent > 0) {
                                toast.warning(
                                    `${String(inconsistent)} asignación(es) quedaron inconsistentes tras el cambio de alcance — revisa y reasigna.`,
                                    { duration: 10000 },
                                );
                            }
                        },
                        onError: handleApiError,
                    },
                );
                return;
            }
        }
    };

    const confirmDelete = (): void => {
        if (!pendingDelete) return;
        const key = pendingDelete.permissionKey;
        deleteMut.mutate(key, {
            onSuccess: () => {
                toast.success('Permiso eliminado');
                setPendingDelete(null);
            },
            onError: (err) => {
                handleApiError(err);
                setPendingDelete(null);
            },
        });
    };

    return (
        <div className="space-y-4">
            <Banner variant="info">
                Los cambios del catálogo se aplican EN VIVO. Agregar o editar un permiso impacta de
                inmediato a los tenants onboardeados de esta aplicación.
            </Banner>

            <PermissionCatalogStep value={value} onChange={handleChange} levels={levels} />

            <ConfirmDialog
                open={pendingDelete !== null}
                onOpenChange={(o) => {
                    if (!o) setPendingDelete(null);
                }}
                title="Eliminar permiso del catálogo"
                description={
                    <span>
                        Vas a eliminar el permiso{' '}
                        <strong>
                            {pendingDelete?.displayName || pendingDelete?.permissionKey}
                        </strong>{' '}
                        del catálogo de esta aplicación. Esto corta el acceso{' '}
                        <strong>EN VIVO</strong> a ese permiso para{' '}
                        <strong>todos los tenants ya onboardeados</strong> de esta app y todos sus
                        usuarios: el techo de cada usuario es rol ∩ catálogo, recalculado en cada
                        request, sin aviso ni vuelta atrás. ¿Continuar?
                    </span>
                }
                confirmLabel="Eliminar permiso"
                destructive
                loading={deleteMut.isPending}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
